import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, inject } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { NgbInputDatepickerConfig } from '@ng-bootstrap/ng-bootstrap';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { akitaDevtools } from '@datorama/akita';
import { environment } from 'src/environments/environment';
import { HttpDEBUGInterceptor } from './debug-endpoints';
import { DistributorComponent } from './default/distributor/distributor.component';
import { RaygunModule } from './raygun/raygun.module';
import { AppRoutingModule } from './routing/app.routing.module';
import { NoContentComponent } from './shared/components/no-content/no-content.component';
import { ShareModule } from './shared/modules/shared.module';
import { DEFAULT_TIMEOUT, HttpTimeoutInterceptor } from './shared/utils/httpTimeoutInterceptor/httpTimeout.interceptor';
import { CURRENCY_MASK_CONFIG, CurrencyMaskConfig, CurrencyMaskDirective } from './shared/directives/currency-mask.directive';
import { DivisionSelectComponent } from './default/division-select/division-select.component';

if (!environment.production) {
  akitaDevtools();
}

const customCurrencyMaskConfig: CurrencyMaskConfig = {
  align: 'left',
  allowNegative: false,
  allowZero: true,
  decimal: '.',
  precision: 2,
  prefix: '$',
  suffix: '',
  thousands: ',',
  nullable: true,
};

@NgModule({
  imports: [BrowserAnimationsModule, ShareModule, AppRoutingModule, RaygunModule, NoContentComponent, DistributorComponent, DivisionSelectComponent, CurrencyMaskDirective],
  providers: [
    { provide: CURRENCY_MASK_CONFIG, useValue: customCurrencyMaskConfig },
    // App-wide dialog defaults. Only autoFocus is defaulted (46 of ~57 dialogs
    // already pass autoFocus:false; making it the default keeps focus behavior
    // consistent and lets those call sites drop the repeated option over time).
    // Deliberately NO default width — dialogs size themselves (Terms of Use,
    // video players, and federated modals rely on their own width), so a blanket
    // width would distort them. Per-dialog `width` still overrides this.
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { autoFocus: false } },
    [{ provide: HTTP_INTERCEPTORS, useClass: HttpTimeoutInterceptor, multi: true }, ...(environment.enableDebugInterceptors ? [{ provide: HTTP_INTERCEPTORS, useClass: HttpDEBUGInterceptor, multi: true }] : [])],
    [{ provide: DEFAULT_TIMEOUT, useValue: 300000 }],
  ],
})
export class AppModule {
  constructor() {
    const self = inject(AppModule, { optional: true, skipSelf: true });

    if (self) {
      throw new Error('AppModule is already loaded. Do not import AppModule.');
    }

    // Render every ngb-datepicker popup attached to <body> so Popper positions it
    // against its input and it escapes overflow/clipping parents (mat-form-field,
    // dialogs, cards). This is the app-wide replacement for the per-page
    // transform/top/left/inset positioning hacks that broke on the ng-bootstrap
    // upgrade. NgbInputDatepicker reads its container default from
    // NgbInputDatepickerConfig at construction. Popup styling lives in
    // src/assets/styles/_datepicker.scss.
    inject(NgbInputDatepickerConfig).container = 'body';
  }
}
